import { DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import { Button, Popconfirm, Space, Table, Tooltip } from "antd";
import moment from "moment";
import { useMemo } from "react";
import { calculatePenaltyPoint, statusFor, buildSubcategoryMap, getSubcategoryMultiplier } from "./buildInspectionPayload";
import { DISPLAY_DATE_FORMAT } from "./constants";

/**
 * @param {import("./types").InspectionRecord} record
 * @returns {import("./types").InspectionDetail[]}
 */
export const getRecordDetails = (record) =>
  (Array.isArray(record?.inspection_details) && record.inspection_details) ||
  (Array.isArray(record?.details) && record.details) ||
  [];

const blankIfEmpty = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (Number.isFinite(n) && n === 0) return "";
  return value;
};

const formatNumber = (value) => {
  const n = Number(value);
  if (value === null || value === undefined || value === "" || !Number.isFinite(n)) return "";
  return Number.isInteger(n) ? n : n.toFixed(2);
};

const formatDate = (value) => (value ? moment(value).format(DISPLAY_DATE_FORMAT) : "");

const headClass = (className) => () => ({ className, style: { color: "#000" } });

/** scroll.y switches antd to table-layout: fixed, so every leaf column needs an explicit width. */
const COLUMN_WIDTHS = {
  sl: 60,
  supplier: 160,
  buyer: 170,
  style: 150,
  received_qty: 90,
  received_date: 105,
  received_roll: 70,
  inspection_date: 105,
  color: 160,
  inspected_roll: 85,
  tag_length: 80,
  tag_actual_length: 80,
  tag_width: 80,
  tag_actual_width: 80,
  defect_point: 44,
  total_defect: 75,
  penalty_point: 80,
  status: 80,
  action: 100,
};

const HEADER_CHAR_WIDTH = 9.5;
const HEADER_PADDING = 24;
const HEADER_MAX_WIDTH = 160;

/**
 * Sub-columns share the category header, so a category with few points (e.g. only "1")
 * must widen them enough to fit its name on at most two lines.
 * @param {import("./types").DefectCategory} category
 * @returns {number}
 */
const defectPointWidth = (category) => {
  const count = Math.max(1, category.subcategories.length);
  const longestWord = Math.max(...String(category.name).split(/\s+/).map((w) => w.length));
  const nameWidth = Math.min(
    HEADER_MAX_WIDTH,
    Math.ceil(Math.max(longestWord, Math.ceil(String(category.name).length / 2)) * HEADER_CHAR_WIDTH) +
      HEADER_PADDING,
  );
  return Math.max(COLUMN_WIDTHS.defect_point, Math.ceil(nameWidth / count));
};

/**
 * @param {import("./types").InspectionRecord} record
 * @param {Record<string, any>} [subMap]
 * @returns {{totalDefect:number, penaltyPoint:number, status:""|"Pass"|"Fail"}}
 */
export const summarizeRecord = (record, subMap = {}) => {
  const details = getRecordDetails(record).filter((d) => Number(d.defect_value) > 0);
  if (!details.length) return { totalDefect: 0, penaltyPoint: 0, status: "" };
  const totalDefect = details.reduce((sum, d) => {
    const val = Number(d.defect_value) || 0;
    if (val <= 0) return sum;
    const subId = d.defect_subcategory_id;
    const subName = d.defect_subcategory?.name ?? d.defectSubcategory?.name ?? subMap[String(subId)]?.name;
    const multiplier = getSubcategoryMultiplier(subName);
    return sum + multiplier * val;
  }, 0);
  const penaltyPoint = calculatePenaltyPoint(totalDefect, record.tag_actual_length, record.tag_actual_width);
  return { totalDefect, penaltyPoint, status: statusFor(penaltyPoint) };
};

/**
 * @param {Object} props
 * @param {import("./types").InspectionRecord[]} props.rows
 * @param {import("./types").DefectCategory[]} props.defectCategories
 * @param {boolean} props.loading
 * @param {{current:number, pageSize:number, total:number}} props.pagination
 * @param {(page:number, pageSize:number) => void} props.onPageChange
 * @param {(record: import("./types").InspectionRecord) => void} props.onEdit
 * @param {(record: import("./types").InspectionRecord) => void} props.onDelete
 * @param {boolean} props.canEdit
 * @param {boolean} props.canDelete
 */
const InspectionReportTable = ({
  rows,
  defectCategories,
  loading,
  pagination = false,
  onPageChange,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
}) => {
  const subcategoryMap = useMemo(() => buildSubcategoryMap(defectCategories), [defectCategories]);

  const columns = useMemo(() => {
    const info = headClass("fi-head-info");
    const length = headClass("fi-head-length");
    const width = headClass("fi-head-width");
    const defect = headClass("fi-head-defect");
    const result = headClass("fi-head-result");
    const action = headClass("fi-head-action");

    const defectGroup = {
      title: "DEFECTS",
      key: "defects",
      onHeaderCell: defect,
      children: defectCategories.map((category) => ({
        title: category.name,
        key: `defect-category-${category.id}`,
        onHeaderCell: defect,
        children: category.subcategories.map((sub) => ({
          title: sub.description ? <Tooltip title={sub.description}>{sub.name}</Tooltip> : sub.name,
          key: `defect-sub-${sub.id}`,
          align: "center",
          width: defectPointWidth(category),
          className: "fi-defect-cell",
          onHeaderCell: defect,
          render: (_, record) => {
            const detail = getRecordDetails(record).find(
              (d) => Number(d.defect_subcategory_id) === Number(sub.id),
            );
            return blankIfEmpty(formatNumber(detail?.defect_value));
          },
        })),
      })),
    };

    return [
      {
        title: "SL NO",
        key: "sl",
        width: COLUMN_WIDTHS.sl,
        align: "center",
        fixed: "left",
        onHeaderCell: info,
        render: (_, __, index) =>
          pagination?.pageSize ? (pagination.current - 1) * pagination.pageSize + index + 1 : index + 1,
      },
      {
        title: "Supplier Name",
        key: "supplier",
        width: COLUMN_WIDTHS.supplier,
        onHeaderCell: info,
        render: (_, r) => r.supplier?.name || "",
      },
      {
        title: "Buyer",
        key: "buyer",
        width: COLUMN_WIDTHS.buyer,
        onHeaderCell: info,
        render: (_, r) => r.buyer?.name || r.sub_contract_factory?.name || "",
      },
      {
        title: "Style",
        key: "style",
        width: COLUMN_WIDTHS.style,
        onHeaderCell: info,
        render: (_, r) => r.style?.style_no || "",
      },
      {
        title: "Received Quantity",
        key: "received_qty",
        width: COLUMN_WIDTHS.received_qty,
        align: "right",
        onHeaderCell: info,
        render: (_, r) => formatNumber(r.received_qty),
      },
      {
        title: "Received Date",
        key: "received_date",
        width: COLUMN_WIDTHS.received_date,
        onHeaderCell: info,
        render: (_, r) => formatDate(r.received_date),
      },
      {
        title: "Total Roll",
        key: "received_roll",
        width: COLUMN_WIDTHS.received_roll,
        align: "center",
        onHeaderCell: info,
        render: (_, r) => formatNumber(r.received_roll),
      },
      {
        title: "Inspection Date",
        key: "inspection_date",
        width: COLUMN_WIDTHS.inspection_date,
        onHeaderCell: info,
        render: (_, r) => formatDate(r.inspection_date),
      },
      {
        title: "Inspected Fabric Color",
        key: "color",
        width: COLUMN_WIDTHS.color,
        onHeaderCell: info,
        render: (_, r) => r.color?.name || "",
      },
      {
        title: "Inspected Roll No.",
        key: "inspected_roll",
        width: COLUMN_WIDTHS.inspected_roll,
        align: "center",
        onHeaderCell: info,
        render: (_, r) => formatNumber(r.inspected_roll),
      },
      {
        title: "TAG-Length",
        key: "tag_length",
        width: COLUMN_WIDTHS.tag_length,
        align: "right",
        onHeaderCell: length,
        render: (_, r) => formatNumber(r.tag_length),
      },
      {
        title: "ACTUAL-Length",
        key: "tag_actual_length",
        width: COLUMN_WIDTHS.tag_actual_length,
        align: "right",
        onHeaderCell: length,
        render: (_, r) => formatNumber(r.tag_actual_length),
      },
      {
        title: "TAG-Width",
        key: "tag_width",
        width: COLUMN_WIDTHS.tag_width,
        align: "right",
        onHeaderCell: width,
        render: (_, r) => formatNumber(r.tag_width),
      },
      {
        title: "ACTUAL-Width",
        key: "tag_actual_width",
        width: COLUMN_WIDTHS.tag_actual_width,
        align: "right",
        onHeaderCell: width,
        render: (_, r) => formatNumber(r.tag_actual_width),
      },
      ...(defectCategories.length ? [defectGroup] : []),
      {
        title: "Total Defect",
        key: "total_defect",
        width: COLUMN_WIDTHS.total_defect,
        align: "center",
        onHeaderCell: result,
        render: (_, r) => blankIfEmpty(summarizeRecord(r, subcategoryMap).totalDefect),
      },
      {
        title: "Penalty Point",
        key: "penalty_point",
        width: COLUMN_WIDTHS.penalty_point,
        align: "center",
        onHeaderCell: result,
        render: (_, r) => blankIfEmpty(formatNumber(summarizeRecord(r, subcategoryMap).penaltyPoint)),
      },
      {
        title: "Pass/Fail",
        key: "status",
        width: COLUMN_WIDTHS.status,
        align: "center",
        onHeaderCell: result,
        render: (_, r) => {
          const { status } = summarizeRecord(r, subcategoryMap);
          if (!status) return "";
          return (
            <span className={`fi-badge ${status === "Fail" ? "fi-badge-fail" : "fi-badge-pass"}`}>
              {status}
            </span>
          );
        },
      },
      {
        title: "Action",
        key: "action",
        align: "center",
        width: COLUMN_WIDTHS.action,
        fixed: "right",
        onHeaderCell: action,
        render: (_, record) => (
          <Space size={4}>
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditTwoTone style={{ fontSize: 18 }} />}
                disabled={!canEdit}
                onClick={() => onEdit(record)}
              />
            </Tooltip>
            <Popconfirm
              title="Delete this fabric inspection?"
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
              disabled={!canDelete}
              onConfirm={() => onDelete(record)}
            >
              <Tooltip title="Delete">
                <Button
                  type="text"
                  icon={<DeleteTwoTone twoToneColor="#f5222d" style={{ fontSize: 18 }} />}
                  disabled={!canDelete}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ];
  }, [defectCategories, subcategoryMap, pagination, canEdit, canDelete, onEdit, onDelete]);

  const scrollX = useMemo(() => {
    const defectWidth = defectCategories.reduce(
      (sum, c) => sum + c.subcategories.length * defectPointWidth(c),
      0,
    );
    const { defect_point: _pointWidth, ...fixedColumns } = COLUMN_WIDTHS;
    return Object.values(fixedColumns).reduce((sum, w) => sum + w, 0) + defectWidth;
  }, [defectCategories]);

  return (
    <Table
      className="fabric-inspection-report"
      bordered
      size="small"
      rowKey="id"
      loading={loading}
      dataSource={rows}
      columns={columns}
      scroll={{ x: scrollX, y: "calc(100vh - 340px)" }}
      pagination={
        pagination
          ? {
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: false,
              showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total}`,
              onChange: onPageChange,
            }
          : false
      }
    />
  );
};

export default InspectionReportTable;
